import React, { Component } from 'react';
import { withRouter } from 'react-router';

import { Grid } from 'components/grid';
import { I18n, translate } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import { withShellContext } from 'hoc/withShellContext';
import { inject, observer } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import { ViewEditSubtitles } from 'modules/app/services/datahubEvents/media';
import { ContentBasePage } from 'modules/content';
import { FeatureToggles } from 'modules/featureToggle';
import { Player } from 'modules/player/components';
import PlayerMessageDispatcher from 'modules/player/components/PlayerMessageDispatcher';
import { ERROR_NO_CUES, SUBTITLE_STATUS } from 'modules/subtitle/constants';
import { UiService } from 'modules/ui';
import { ALERT } from 'modules/ui/constants';
import PropTypes from 'prop-types';

import SubtitleCueEditor from './SubtitleCueEditor';
import SubtitleEnable from './SubtitleEnable';
import SubtitleTimeline from './SubtitleTimeline';

import SubtitleActions from '../SubtitleActions';

import './SubtitleEditor.scss';

export const EDIT_SUBTITLE = 'player_subtitles.subtitle_editor.edit_subtitle';

@withShellContext
@withRouter
@translate()
@inject((stores) => ({
  languageCode: stores.SubtitleStore.languageCode,
  status: stores.SubtitleStore.status,
  isCreateModuleConceptEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CreateModuleConcept
  )
}))
@observer
class SubtitleEditor extends Component {
  static propTypes = {
    languageCode: PropTypes.string,
    status: PropTypes.string,
    history: PropTypes.object,
    match: PropTypes.object,
    isMembership: PropTypes.bool
  };

  static defaultProps = {
    playerMessageDispatcher: new PlayerMessageDispatcher()
  };

  componentDidMount() {
    SubtitleActions.setSubtitleTrack(this.mediaId, this.subtitleId);
    SubtitleActions.setPlayer(this.props.playerMessageDispatcher);

    DatahubEventsService.sendEvent({
      schema: new ViewEditSubtitles({
        arrivedFrom: window.location.href
      })
    });
  }

  componentWillUnmount() {
    SubtitleActions.resetSubtitleCues();
    SubtitleActions.resetCurrentCue();
  }

  get subtitleId() {
    const { match } = this.props;
    const { params } = match;

    return params.subtitleId;
  }

  get mediaId() {
    const { match } = this.props;
    const { params } = match;

    return params.mediaId;
  }

  get isActive() {
    const { status } = this.props;

    return status === SUBTITLE_STATUS.ACTIVE;
  }

  backHandler = () => {
    const { history, isCreateModuleConceptEnabled, isMembership } = this.props;

    if (isCreateModuleConceptEnabled && isMembership) {
      history.push(`${ROUTES.CONTENT.LIST}${window.location.search}`);

      return;
    }

    history.goBack();
  };

  render() {
    const { languageCode, status, playerMessageDispatcher } = this.props;

    return (
      <ContentBasePage className="content__grid" backHandler={this.backHandler}>
        <ContentBasePage.Header title={EDIT_SUBTITLE}>
          <ContentBasePage.Header.Actions>
            <SubtitleEnable
              mediaId={this.mediaId}
              id={this.subtitleId}
              status={status}
            />
          </ContentBasePage.Header.Actions>
        </ContentBasePage.Header>

        <Grid block>
          {languageCode && (
            <h2>
              <I18n label={`supported_languages.${languageCode}`} />
            </h2>
          )}

          <Player
            mediaId={this.mediaId}
            playerMessageDispatcher={playerMessageDispatcher}
          />

          <SubtitleTimeline />
          <SubtitleCueEditor />
        </Grid>

        {this.renderFooter()}
      </ContentBasePage>
    );
  }

  renderFooter() {
    return (
      <ContentBasePage.Footer
        onSubmit={this.updateSubtitle}
        cancelHandler={this.backHandler}
      />
    );
  }

  handleUpdateSubtitleError = (error) => {
    const { t } = this.props;

    switch (error?.message) {
      case ERROR_NO_CUES:
        UiService.setAlertError(
          'must_not_be_empty',
          { field: t('player_subtitles.add_new_subtitle') },
          ALERT.TIMEOUT.LONG
        );
        break;
      default:
        UiService.setAlertError('error_try_again');
    }
  };

  updateSubtitle = async () => {
    try {
      SubtitleActions.validateSubtitle();
      await SubtitleActions.updateSubtitle(this.mediaId, this.subtitleId);
      this.backHandler();
    } catch (error) {
      this.handleUpdateSubtitleError(error);
    }
  };

  approveSubtitle = () => {
    SubtitleActions.approveSubtitle(this.mediaId, this.subtitleId);
  };

  toggleActive = () => {
    const { status } = this.props;

    SubtitleActions.toggleSubtitle(status, this.mediaId, this.subtitleId);
  };
}

export default SubtitleEditor;
